<script setup>
import { computed } from 'vue'
import Badge from '@/components/Badge.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Spinner from '@/components/ui/Spinner.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconLink from '@/components/icon/IconLink.vue'
import IconInfo from '@/components/icon/IconInfo.vue'
import IconCheckInCircle from '@/components/icon/IconCheckInCircle.vue'
import IconLinkExternal from '@/components/icon/IconLinkExternal.vue'
import { usePlatformListContext } from './platformListContext'
import { getYoutubeChannelUrl } from '@/Utils/platformStatus'

const props = defineProps({
	platform: {
		type: Object,
		required: true,
	},
})

const ctx = usePlatformListContext()

const mediaPulse = computed(() => ctx.mediaPulse)
const fbOauth = computed(() => ctx.facebookOauthState?.[props.platform.fbAccount] || { isLoading: false, errorMessage: '' })
const ytOauth = computed(() => ctx.youtubeOauthState?.[props.platform.ytAccount] || { isLoading: false, errorMessage: '' })

const showPushStatus = computed(() =>
	props.platform.enabled && mediaPulse.value && mediaPulse.value.push_stats
)
const isYoutubeEvents = computed(() => props.platform.template === 'youtube events')
const isFacebookLive = computed(() => props.platform.template === 'facebook live')
const showOpenLink = computed(() =>
	props.platform.serviceChannelUrl && !( !props.platform.isTokenValid && isYoutubeEvents.value )
)
</script>

<template>
	<div class="mobile-destination-status flex flex-wrap items-center gap-x-2 w-full min-w-0">
		<StatusPill
			v-if="showPushStatus && platform.connected"
			variant="success"
			size="sm"
			class="md:hidden"
		>
			Connected
		</StatusPill>
		<StatusPill
			v-else-if="showPushStatus"
			variant="warning"
			pulse
			size="sm"
			class="md:hidden"
		>
			Connecting…
		</StatusPill>
		<Badge v-if="platform.reusableDestination">
			<icon-base width="12" height="12" icon-name="link" classIcon=" mr-2">
				<icon-link />
			</icon-base>
			Reusable destinations
		</Badge>
		<span
			v-if="platform.linkedServiceCreds && platform.isTokenValid"
			class="platform-item__status flex items-center text-tiny"
		>
			<icon-base class="mr-1 text-[#65C374]" fill="none">
				<icon-check-in-circle />
			</icon-base>
			Linked
		</span>
		<span
			v-if="!platform.isTokenValid && isFacebookLive"
			class="platform-item__status platform-item__status--error flex items-center text-error text-tiny flex-wrap"
		>
			<template v-if="!fbOauth.isLoading">
				<icon-base class="text-danger" fill="none">
					<icon-info />
				</icon-base>
				The API connection has expired.
				<a class="platform-item__status--error" href="" @click.prevent="ctx.getFacebookUrl(platform.fbAccount)">
					<u>Reconnect</u>
				</a>
			</template>
			<template v-if="fbOauth.errorMessage">
				<br>Reconnection failed: {{ fbOauth.errorMessage }}
			</template>
			<div v-if="fbOauth.isLoading">
				<span class="hidden lg:block">
					<Spinner text="Connecting..." classes="text-dark-8 my-3" spinner-color="var(--c-dark-8)" spinner-size="15px" />
				</span>
				<span class="block lg:hidden">
					<Spinner text="" classes="text-dark-8 my-3" spinner-color="var(--c-dark-8)" spinner-size="15px" />
				</span>
			</div>
		</span>
		<span
			v-if="!platform.isTokenValid && isYoutubeEvents"
			class="platform-item__status platform-item__status--error"
		>
			<template v-if="!ytOauth.isLoading">
				<icon-base class="mx-2 text-danger" fill="none">
					<icon-info />
				</icon-base>
				The API connection has expired. Please remove this and re-add the platform.
			</template>
			<template v-if="ytOauth.errorMessage">
				<br>Reconnection failed: {{ ytOauth.errorMessage }}
			</template>
			<div v-if="ytOauth.isLoading">
				<span class="hidden lg:block">
					<Spinner text="Connecting..." classes="text-dark-8 my-3" spinner-color="var(--c-dark-8)" spinner-size="15px" />
				</span>
				<span class="block lg:hidden">
					<Spinner text="" classes="text-dark-8 my-3" spinner-color="var(--c-dark-8)" spinner-size="15px" />
				</span>
			</div>
		</span>
		<div
			v-if="isYoutubeEvents && platform.serviceMeta && platform.serviceMeta.enableAdsOnStream"
			class="flex items-center text-tiny"
		>
			<icon-base class="mr-1 text-[#65C374]" fill="none">
				<icon-check-in-circle />
			</icon-base>
			Ads enabled
		</div>
		<a
			v-if="showOpenLink"
			:href="getYoutubeChannelUrl(platform.serviceChannelUrl)"
			target="_blank"
			class="platform-item__link flex items-center text-tiny"
		>
			Open
			<icon-base class="ml-1" fill="none">
				<icon-link-external />
			</icon-base>
		</a>
	</div>
</template>
